import React, { useEffect, useMemo, useState } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { withTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import parseJSON from 'date-fns/parseJSON'

import { programs as programsResource } from '../../redux/actions/resource'

import { getAllPrograms, getCurrentProject } from '../../redux/reducers'

import Action from '../../components/Action'
import { confirm } from '../../components/Alert'
import Icon from '../../components/Icon'
import ListToolbar from '../../components/ListToolbar'
import Table, { Body, Column, Head, Header, Row } from '../../components/Table'
import Tile, { Grid, GridItem, LabelledValue } from '../../components/Tile'
import { TextLink } from '../../components/type'
import Toolbar from '../../components/Toolbar'
import { addFlashMessage } from '../../redux/actions'
import DragHandle from '../../components/drag-and-drop/DragHandle'
import SortableItem from '../../components/drag-and-drop/SortableItem'
import SortableList from '../../components/drag-and-drop/SortableList'
import useSortableOrder from '../../components/drag-and-drop/useSortableOrder'

const ProgramTitle = styled.span`
  display: block;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  text-align: center;
  overflow-wrap: anywhere;
`

const ProgramListItem = withRouter(
  ({
    id,
    name,
    publishAttempt,
    onDeleteProgram,
    disabled,
    t,
    sortOrder,
    state,
    summary,
    startsAt,
    endsAt,
    history,
  }) => {
    const failed = (publishAttempt || {}).succeeded === false
    const title = `${name}${failed ? ' ⚠️' : ''}`
    const active = ['running', 'expiring'].includes(state) ? 1 : 0

    return (
      <SortableItem id={id} disabled={disabled} container={GridItem}>
        {({
          attributes,
          disabled: sortableDisabled,
          listeners,
          setActivatorNodeRef,
        }) => (
          <Tile
            title={
              <ProgramTitle>
                <DragHandle
                  ref={setActivatorNodeRef}
                  type="button"
                  disabled={sortableDisabled}
                  aria-label={t('actions:program.reorder')}
                  {...attributes}
                  {...listeners}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Icon small name="bars" bordered={false} />
                </DragHandle>
                {title}
              </ProgramTitle>
            }
            onClick={() => history.push(`/programs/${id}`)}
            footer={`${t('format:dateTimeShort', {
              date: parseJSON(startsAt),
            })} - ${t('format:dateTimeShort', { date: parseJSON(endsAt) })}`}
            centered
            actions={
              <Action
                onClick={(e) => {
                  onDeleteProgram(id)
                  e.stopPropagation()
                }}
              >
                <Icon name="delete" color="#fff" border="#fff" />
              </Action>
            }
          >
            <LabelledValue
              value={active}
              label={t('attributes:program:active')}
            />
            {(summary || []).map(({ entity_type, scope, count }, index) => {
              const txt = `${
                scope == null ? 'models:' : 'attributes:'
              }${entity_type}${
                scope == null
                  ? ''
                  : scope == 'suspended'
                  ? ':modes:1'
                  : ':modes:0'
              }`
              return (
                <LabelledValue
                  key={index}
                  value={count}
                  label={t(txt, { count: count })}
                />
              )
            })}
          </Tile>
        )}
      </SortableItem>
    )
  }
)

const RunningIndicator = styled.span`
  display: inline-block;
  height: 1rem;
  width: 1rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.brandSecondary};
`

const comparePrograms = (first, second) => {
  const firstOrder = Number(first.sortOrder)
  const secondOrder = Number(second.sortOrder)
  const normalizedFirstOrder = Number.isFinite(firstOrder)
    ? firstOrder
    : Number.MAX_SAFE_INTEGER
  const normalizedSecondOrder = Number.isFinite(secondOrder)
    ? secondOrder
    : Number.MAX_SAFE_INTEGER

  return normalizedFirstOrder - normalizedSecondOrder || first.id - second.id
}

const ProgramList = ({ t, history }) => {
  const programs = useSelector(getAllPrograms)
  const currentProject = useSelector(getCurrentProject)
  const projectId = currentProject?.id
  const dispatch = useDispatch()
  const [searchActive, setSearchActive] = useState(false)
  const sortedPrograms = useMemo(
    () => [...(programs || [])].sort(comparePrograms),
    [programs]
  )
  const {
    isSaving: reordering,
    onDragEnd,
    orderedIds: orderedProgramIds,
  } = useSortableOrder({
    items: sortedPrograms,
    updatePosition: (programId, sortOrder) =>
      dispatch(
        programsResource.update(programId, {}, { sort_order: sortOrder })
      ).promise,
    reload: () => dispatch(programsResource.list({ projectId })),
    onError: () =>
      dispatch(addFlashMessage('Fehler', t('errors:program.couldNotReorder'))),
    disabled: searchActive,
  })
  const orderedPrograms = orderedProgramIds
    .map((programId) =>
      sortedPrograms.find(
        ({ id: currentId }) => String(currentId) === String(programId)
      )
    )
    .filter(Boolean)
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    dispatch(programsResource.list({ projectId }))
  }, [dispatch, projectId])

  const onSearch = (search) => {
    const active = search?.toString().length > 0
    setSearchActive(active)

    if (currentProject) {
      dispatch(programsResource.list({ projectId, search }))
    }
  }

  const onDeleteProgram = async (id) => {
    if (await confirm('Möchtest du dieses Programm wirklich löschen?')) {
      dispatch(programsResource.destroy(id))
    }
  }

  return (
    <>
      <Toolbar
        title={t('models:program', { count: 0 })}
        onBack={() => history.push('/dashboard')}
      >
        <Action to="/programs/new">
          <Icon name="add" />
        </Action>
      </Toolbar>
      <ListToolbar onSearch={onSearch}>
        {({ showTiles, searchParams }) => {
          const filtered = searchActive || searchParams.length > 0

          return showTiles ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <Grid>
                <SortableList
                  items={orderedPrograms.map(({ id }) => id)}
                  strategy={rectSortingStrategy}
                >
                  {orderedPrograms.map((program) => {
                    return (
                      <ProgramListItem
                        key={program.id}
                        {...program}
                        onDeleteProgram={onDeleteProgram}
                        disabled={reordering || filtered}
                        t={t}
                      />
                    )
                  })}
                </SortableList>
              </Grid>
            </DndContext>
          ) : (
            <Table spacious divided>
              <Head>
                <Row>
                  <Header colSpan={2}>
                    {t('models:program', { count: 0 })}
                  </Header>
                </Row>
              </Head>
              <Body>
                {orderedPrograms.map(({ id, name, state }, index) => {
                  return (
                    <Row key={index}>
                      <Column>
                        <TextLink to={`/programs/${id}`}>
                          {name}{' '}
                          {['running', 'expiring'].includes(state) && (
                            <RunningIndicator />
                          )}
                        </TextLink>
                      </Column>
                      <Column actions>
                        <Action horizontal to={`/programs/${id}`}>
                          <Icon small name="view" />
                        </Action>
                        <Action horizontal to={`/programs/${id}/edit`}>
                          <Icon small name="edit" />
                        </Action>
                        <Action
                          horizontal
                          onClick={(e) => {
                            onDeleteProgram(id)
                            e.stopPropagation()
                          }}
                        >
                          <Icon small name="delete" />
                        </Action>
                      </Column>
                    </Row>
                  )
                })}
              </Body>
            </Table>
          )
        }}
      </ListToolbar>
    </>
  )
}

export default withRouter(withTranslation()(ProgramList))
